import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, Building2, Hospital, Sparkles, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = {
  agency: 'Care Provider',
  discharge_planner: 'Healthcare Facility',
};

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/org/invitations/by-token/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setInvitation(data);
        if (data.first_name) setFirstName(data.first_name);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || 'This invitation link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || password.length < 8) {
      toast.error('Please fill all required fields. Password must be 8+ characters.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/org/invitations/accept`, {
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
      });
      toast.success("Account created. You're in!");
      navigate('/auth?accepted=1');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not accept the invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const OrgIcon = invitation?.role_to_grant === 'discharge_planner' ? Hospital : Building2;

  return (
    <div className="min-h-screen bg-cn-bone-50 dark:bg-cn-ink-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg" data-testid="invite-accept-page">
        <Link to="/" className="flex items-center justify-center mb-6">
          <CaraLoomLogo />
        </Link>

        {loading && (
          <div className="cn-card p-8 text-center" data-testid="invite-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-cn-primary" />
            <p className="text-sm text-cn-text-muted mt-3">Checking your invitation…</p>
          </div>
        )}

        {!loading && error && (
          <div className="cn-card p-8 text-center" data-testid="invite-error">
            <AlertCircle className="w-8 h-8 mx-auto text-cn-terracotta-600 mb-3" />
            <h1 className="font-display text-xl font-bold text-cn-text dark:text-white mb-2">
              We couldn&apos;t open this invitation
            </h1>
            <p className="text-sm text-cn-text-muted mb-5">{error}</p>
            <Link to="/auth" className="cn-btn cn-btn-primary inline-flex items-center gap-2">
              Go to sign in
            </Link>
          </div>
        )}

        {!loading && !error && invitation && (
          <div className="cn-card p-8" data-testid="invite-form-card">
            <div className="flex items-center gap-3 mb-1">
              <span className="w-10 h-10 rounded-xl bg-cn-primary/15 flex items-center justify-center text-cn-primary">
                <OrgIcon className="w-5 h-5" />
              </span>
              <span className="text-[11px] uppercase tracking-wider font-bold text-cn-primary" data-testid="invite-role">
                {ROLE_LABEL[invitation.role_to_grant] || invitation.role_to_grant}
              </span>
            </div>
            <h1 className="font-display text-2xl font-bold text-cn-text dark:text-white mt-2" data-testid="invite-headline">
              Join <span className="text-cn-primary">{invitation.org_name}</span> on CaraLoom
            </h1>
            <p className="text-sm text-cn-text-muted mt-1" data-testid="invite-inviter">
              {invitation.inviter_name} invited <span className="font-mono">{invitation.email}</span> to join their team.
            </p>

            {invitation.note && (
              <p className="mt-4 px-4 py-3 bg-cn-bone-100 dark:bg-cn-ink-800/50 rounded-lg text-sm text-cn-text italic" data-testid="invite-note">
                “{invitation.note}”
              </p>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="iv-first" className="text-[10px] uppercase font-bold text-cn-text-muted">First name *</Label>
                  <Input id="iv-first" value={firstName}
                         onChange={(e) => setFirstName(e.target.value)} required
                         className="mt-1" data-testid="invite-first-name" />
                </div>
                <div>
                  <Label htmlFor="iv-last" className="text-[10px] uppercase font-bold text-cn-text-muted">Last name *</Label>
                  <Input id="iv-last" value={lastName}
                         onChange={(e) => setLastName(e.target.value)} required
                         className="mt-1" data-testid="invite-last-name" />
                </div>
              </div>
              <div>
                <Label htmlFor="iv-password" className="text-[10px] uppercase font-bold text-cn-text-muted">Create a password *</Label>
                <Input id="iv-password" type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)} required minLength={8}
                       placeholder="At least 8 characters" className="mt-1"
                       data-testid="invite-password" />
              </div>
              <Button type="submit" disabled={submitting}
                      className="cn-btn cn-btn-primary !py-3 w-full" data-testid="invite-submit">
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Creating your account…</>
                  : <><CheckCircle2 className="w-4 h-4 mr-1" /> Accept &amp; create account</>}
              </Button>

              <div className="flex items-center justify-center gap-4 text-[11px] text-cn-text-muted pt-2">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> HIPAA-ready</span>
                <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI-orchestrated</span>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
